import { Suspense, useState } from "react"
import { DateTime } from "luxon"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../../login/loginSelectors"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    fetchPeakHoursSummaryRatePerResource,
    ServiceCenterPeakHoursSummaryReportResponse,
    ServiceCenterStatisticsQuery,
} from "../../statisticsApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { Chart as ChartJS, Tooltip, Legend, LinearScale, CategoryScale } from "chart.js"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { Chart } from "react-chartjs-2"
import { MatrixController, MatrixElement } from "chartjs-chart-matrix"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { generateMatrixData, getChartData, getChartOptions } from "./utils"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { ResourceTypeFilter } from "../common/ResourceTypeFilter"
import { useExportPeakHours } from "../../hooks/useExportPeakHours"
import { useDateTime } from "../../../../../localization/useDateTime"
import { ToolBarContainer } from "../../../../../../shared/components/toolbarContainer/ToolbarContainer"

const selectBookingSummaries = (data: ServiceCenterPeakHoursSummaryReportResponse) => data?.weekDayOccupancyRates || []

ChartJS.register(MatrixController, MatrixElement, Tooltip, Legend, LinearScale, CategoryScale)

export function PeakHoursComponent() {
    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()
    const [selectedResource, setSelectedResource] = useState<ResourceType>(ResourceType.ROOM)
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })

    const bookingSummariesResult = useApiQuery(fetchPeakHoursSummaryRatePerResource, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_PEAK_HOURS_SUMMARY,
        dependencies: {
            resourceType: selectedResource,
            serviceCenterId: serviceCenterId ?? 0,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        select: selectBookingSummaries,
    })

    const { data, xLabels, yLabels } = generateMatrixData(bookingSummariesResult.data!)
    const options = getChartOptions(xLabels, yLabels)
    const matrixData = getChartData(data, l10n)

    const exportPeakHours = useExportPeakHours({
        data: bookingSummariesResult.data,
        startDate: dateState.startDate || today,
        endDate: dateState.endDate || today,
    })

    return (
        <ToolBarContainer
            renderToolbarRightActions={
                <StandardExportButton
                    onClick={() => {
                        exportPeakHours()
                    }}
                />
            }
            renderToolbarLeftActions={
                <>
                    <LocalizedStrict
                        id="service-center-reports-statistic-occupancy-table-date-range-filter"
                        attrs={{ label: true }}
                    >
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} label="Date range" />
                    </LocalizedStrict>
                    <ResourceTypeFilter value={selectedResource} onChange={setSelectedResource} />
                </>
            }
        >
            <Chart type="matrix" data={matrixData} options={options} />
        </ToolBarContainer>
    )
}

export const PeakHours = () => {
    return (
        <QueryErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
                <PeakHoursComponent />
            </Suspense>
        </QueryErrorBoundary>
    )
}
